import {
    POOJA_MODULE_ROLES, COMMON_ROLES,
    EXPENSE_MODULE_ROLES, AUDITORIUM_MODULE_ROLES,
    MEMBERSHIP_MODULE_ROLES, REPORT_MODULE_ACCESS, PAYROLL_MODULE_ROLES, STORE_MODULE_ROLES,
    ACCOUNT_MODULE_ROLES, ACCOUNTANT_ROLES, REPORT_MODULE, POOJA_REPORT_ROLES, RENTAL_MODULE_ROLES, AUCTION_MODULE_ROLES, ACCOMMODATION_MODULE_ROLES,
    BANK_MODULE_ROLES, FUNCTION_HALL_MODULE_ROLES
} from './roles';
import { NgModule } from '@angular/core';
import { Routes } from '@angular/router';
import { AuthenticatedComponent } from './authenticated/authenticated.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { TempleOwnerLoginComponent } from './temple-owner-login/temple-owner-login.component';
import { SearchResolverService } from './resolvers/search-resolver.service';

import { TempleAddressComponent } from './temple-address/temple-address.component';
import { HomeComponent } from './home/home.component';
import { GuestGuard } from 'guards/guest.guard';
import { AuthGuard } from 'guards/auth.guard';

import { ErrorComponent } from './error/error.component';
import { LoginStodaComponent } from './login-stoda/login-stoda.component';
import { BootstrapSetupComponent } from './core/bootstrap-setup/bootstrap-setup.component';
import { TempleRegistrationComponent } from './temple-registration/temple-registration.component';
import { UserRouteAccessGuard } from 'guards/user-route-access-guard';
import { PanchangamResolverService } from './resolvers/panchangam-resolver.service';
import { ExpiryGuard } from '../guards/expiry.guard';

const isTauri = typeof (window as any).__TAURI__ !== 'undefined';
const defaultRoute = isTauri ? 'login-stoda' : 'pooja-booking';

export const routes: Routes = [
    {
        path: 'setup-wizard', component: BootstrapSetupComponent, data: {
            pageTitle: 'SETUP'
        }
    },
    {
        path: 'login-stoda', component: LoginStodaComponent, data: {
            pageTitle: 'LOG IN'
        }
    },
    {
        // WS-DBM Phase 3: first-run registration. login-stoda routes here when the
        // `all` feed comes back with no temple (a freshly installed, empty database).
        path: 'temple-registration', component: TempleRegistrationComponent, data: {
            pageTitle: 'TEMPLE REGISTRATION'
        }
    },
    {
        path: 'pooja-booking',
        loadChildren: () => import('./shivam/app.routes').then(m => m.routes)
    },
    {
        path: 'accommodation-booking',
        loadChildren: () => import('./shivam/app.routes').then(m => m.accommodationRoutes)
    },
    {
        path: 'home', redirectTo: defaultRoute, pathMatch: 'full',
    },
    {
        path: '', redirectTo: defaultRoute, pathMatch: 'full',
    },
    {
        path: 'register', loadChildren: () => import('./register/register.module').then(m => m.RegisterModule)
    },
    {
        path: 'app', component: AuthenticatedComponent, canActivate: [AuthGuard],
        children: [
            {
                path: '', canActivateChild: [AuthGuard], children: [
                    {
                        path: 'dashboard', component: HomeComponent
                    },
                    {
                        path: 'temple-address', component: TempleAddressComponent
                    },
                    {
                        path: 'temple-owner-login', component: TempleOwnerLoginComponent

                    },
                    {
                        path: 'pooja',
                        loadChildren: () => import('./pooja-booking/pooja-booking.module').then(m => m.PoojaBookingModule),
                        data: {
                            authorities: [...POOJA_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'pooja'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]

                    },
                    {
                        // Booking v2 (docs/BOOKING_V2.md): standalone route,
                        // same guards/authorities as `pooja` above. DEFAULT
                        // booking screen since 2026-07-11 (menu + post-login);
                        // the classic screen stays as the rollback entry.
                        path: 'pooja-v2',
                        loadChildren: () => import('./booking-v2/booking-v2.routes').then(m => m.routes),
                        data: {
                            authorities: [...POOJA_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Pooja Booking v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Pooja Enquiry v2 (docs/POOJA_ENQUIRY_V2.md): standalone
                        // route, same guards/authorities as `pooja-v2` above.
                        // Infinite-scroll list + date-range filter + full-port
                        // edit modals. URL-only for owner testing; the classic
                        // enquiry screen stays the default.
                        path: 'pooja-enquiry-v2',
                        loadChildren: () => import('./pooja-enquiry-v2/pooja-enquiry-v2.routes').then(m => m.routes),
                        data: {
                            authorities: [...POOJA_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Pooja Enquiry v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Vazhipadu Head v2 (docs/BOOKING_V2.md): standalone
                        // ADMIN route; same guards/authorities as the legacy
                        // host module `stoda` below (the classic add-pooja
                        // screen lives there under REPORT_MODULE_ACCESS).
                        // DEFAULT since 2026-07-11 (menu entry); classic stays
                        // as the rollback entry.
                        path: 'vazhipadu-v2',
                        loadChildren: () => import('./booking-v2/vazhipadu/vazhipadu.routes').then(m => m.routes),
                        data: {
                            authorities: [...REPORT_MODULE_ACCESS],
                            pageTitle: 'Vazhipadu Head v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Expense / Income billing v2 (docs/MODULES_V2.md,
                        // Batch 1): parallel standalone route gated by the
                        // expense module roles (+ common admin roles), the
                        // module-appropriate set for a billing screen. The
                        // legacy screen at /app/stoda/expense-billing-stoda is
                        // untouched and stays the default. No menu entry yet:
                        // reached by URL (/app/expense-v2) for owner testing.
                        path: 'expense-v2',
                        loadChildren: () => import('./modules-v2/expense/expense.routes').then(m => m.routes),
                        data: {
                            authorities: [...EXPENSE_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Expense Billing v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Income billing v2 (docs/MODULES_V2.md, WS-H): the
                        // Transaction -> Income screen mirroring Expense, with an
                        // income-head dropdown; POSTs the corrected income
                        // double-entry. Legacy /app/stoda/income-billing untouched,
                        // the V1 default. Same guards as the expense/income host.
                        path: 'income-v2',
                        loadChildren: () => import('./modules-v2/income/income.routes').then(m => m.routes),
                        data: {
                            authorities: [...EXPENSE_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Income Billing v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Pooja Stall (material sale) v2 (docs/MODULES_V2.md,
                        // Batch 5): parallel standalone counter material-sale
                        // route. Books through the shared booking outbox, so it
                        // is gated like the booking screen. Legacy /app/stoda/sale
                        // untouched and default. No menu entry yet: reached by URL
                        // (/app/pooja-stall-v2) for owner testing.
                        path: 'pooja-stall-v2',
                        loadChildren: () => import('./modules-v2/pooja-stall/pooja-stall.routes').then(m => m.routes),
                        data: {
                            authorities: [...POOJA_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Pooja Stall v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // WS-R Rental module (V2-only, no legacy sibling):
                        // billing at /app/rental-v2, enquiry + item master as
                        // children; server-side guards are the authority
                        // (rentalBillingAccess / rentalFullAccess).
                        path: 'rental-v2',
                        loadChildren: () => import('./modules-v2/rental/rental.routes').then(m => m.routes),
                        data: {
                            authorities: [...RENTAL_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Rental'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // WS-L Auction (Lelam) module (V2-only, no legacy
                        // sibling): register at /app/auction-v2, lelam stock +
                        // sold-register enquiry as children; server-side
                        // guards are the authority (auctionBillingAccess /
                        // auctionFullAccess).
                        path: 'auction-v2',
                        loadChildren: () => import('./modules-v2/auction/auction.routes').then(m => m.routes),
                        data: {
                            authorities: [...AUCTION_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Auction (Lelam)'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // WS-ACM Accommodation module (V2-only, no legacy
                        // sibling): booking window at /app/accommodation-v2,
                        // rooms master + stay enquiry as children; server-side
                        // guards are the authority (accommodationBillingAccess /
                        // accommodationFullAccess).
                        path: 'accommodation-v2',
                        loadChildren: () => import('./modules-v2/accommodation/accommodation.routes').then(m => m.routes),
                        data: {
                            authorities: [...ACCOMMODATION_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Accommodation'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // WS-FHP Function Hall + Priest module (V2-only, no
                        // legacy sibling): booking at /app/function-hall-v2,
                        // priest booking / both enquiries / masters / settings
                        // as children. Server-side guards are the authority
                        // (functionHallBillingAccess / functionHallFullAccess).
                        // The four booking/enquiry ENTRIES are hidden from the
                        // menu while VENUE_MODULE = AUDITORIUM (D-FHP-9), but
                        // the ROUTES stay registered under both values — the
                        // URL-level rollback the V1/V2 toggle also keeps.
                        path: 'function-hall-v2',
                        loadChildren: () => import('./modules-v2/function-hall/function-hall-v2.routes').then(m => m.routes),
                        data: {
                            authorities: [...FUNCTION_HALL_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Function Hall'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // WS-ACC accounting module (V2). Phase 1 = Manual
                        // Journal ('journal' child): direct-post balanced
                        // Dr/Cr vouchers + register + reversal. Server-side
                        // guards are the authority (accountingWriteAccess
                        // writes / accountVoucherAccess reads); the route
                        // union mirrors the journal-entry menu roles.
                        path: 'accounting-v2',
                        loadChildren: () => import('./modules-v2/accounting/accounting.routes').then(m => m.routes),
                        data: {
                            authorities: [...COMMON_ROLES, ...ACCOUNTANT_ROLES, 'ACCOUNTING_FULL_ACCESS'],
                            pageTitle: 'Accounting'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // WS-IDP Identified Payments (V2). Entry screen at
                        // /app/identified-payment-v2 (bank-transaction billing
                        // roles), the ACCOUNT config screen as the 'setup'
                        // child; server-side guards are the authority
                        // (accountVoucherAccess for config, bank-transaction
                        // billing for posting). Route authorities are the union
                        // so both roles can load the lazy chunk.
                        path: 'identified-payment-v2',
                        loadChildren: () => import('./modules-v2/identified-payment/identified-payment.routes').then(m => m.routes),
                        data: {
                            authorities: [...COMMON_ROLES, ...BANK_MODULE_ROLES, ...ACCOUNTANT_ROLES],
                            pageTitle: 'Identified Payment'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        path: 'account-head',
                        loadChildren: () => import('./administration/account-head/account-head.routes').then(m => m.routes),
                        data: {
                            authorities: ['POOJA_FULL_ACCESS', 'EXPENSE_FULL_ACCESS', ...COMMON_ROLES, ...STORE_MODULE_ROLES],
                            pageTitle: 'Account Head'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        path: 'devotee',
                        loadChildren: () => import('./administration/devotee/devotee.routes').then(r => r.routes),
                        data: {
                            authorities: [...POOJA_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Devotee'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        path: 'store',
                        loadChildren: () => import('./administration/store/store.module').then(m => m.StoreModule),
                        data: {
                            authorities: [...COMMON_ROLES, ...STORE_MODULE_ROLES],
                            pageTitle: 'Store'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        path: 'member-heads',
                        loadChildren: () => import('./administration/member-heads/member-heads.module').then(m => m.MemberHeadsModule),
                        data: {
                            authorities: [...COMMON_ROLES],
                            pageTitle: 'Member Heads'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        path: 'member-product',
                        loadChildren: () => import('./administration/member-product/member-product.module').then(m => m.MemberProductModule),
                        data: {
                            authorities: [...COMMON_ROLES],
                            pageTitle: 'Member Product'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        path: 'add-auditorium',
                        loadChildren: () => import('./administration/add-auditorium/add-auditorium.routes').then(r => r.routes),
                        data: {
                            authorities: [...COMMON_ROLES],
                            pageTitle: 'Auditorium'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        path: 'date-configuration',
                        loadChildren: () => import('./administration/date-configuration/date-configuration.routes').then(r => r.routes),
                        data: {
                            authorities: ['COMPANY_ACCESS', 'DATE_CONFIGURATION_ACCESS'],
                            pageTitle: 'Date Configuration'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },




                    {
                        path: 'employees',
                        loadChildren: () => import('./administration/employee/employee.module').then(m => m.EmployeeModule),
                        data: {
                            authorities: [...COMMON_ROLES],
                            pageTitle: 'Employees'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    }, {
                        // Employee master v2 (docs/MODULES_V2.md, Batch 6): the
                        // representative master-data screen, same
                        // guards/authorities as the legacy `employees` admin
                        // host above. Legacy /app/administration/employee
                        // untouched and default. No menu entry yet: reached by
                        // URL (/app/employee-v2) for owner testing.
                        path: 'employee-v2',
                        loadChildren: () => import('./modules-v2/employee/employee.routes').then(m => m.routes),
                        data: {
                            authorities: [...COMMON_ROLES],
                            pageTitle: 'Employees v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    }, {
                        path: 'expense',
                        loadChildren: () => import('./administration/expense/expense.module').then(m => m.ExpenseModule),
                        data: {
                            authorities: [...EXPENSE_MODULE_ROLES, ...COMMON_ROLES, ...STORE_MODULE_ROLES],
                            pageTitle: 'Expense'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    }, {
                        path: 'vendor',
                        loadChildren: () => import('./administration/vendor/vendor.module').then(m => m.VendorModule),
                        data: {
                            authorities: [...EXPENSE_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Vendor'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        path: 'auditorium',
                        loadChildren: () => import('./auditorium/auditorium.routes').then(r => r.routes),
                        data: {
                            authorities: [...AUDITORIUM_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Auditorium'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Auditorium v2 (docs/MODULES_V2.md, Batch 4): parallel
                        // standalone auditorium-booking route, same
                        // guards/authorities as the legacy `auditorium` host
                        // above. Legacy screens at /app/auditorium/* untouched
                        // and default. No menu entry yet: reached by URL
                        // (/app/auditorium-v2) for owner testing.
                        path: 'auditorium-v2',
                        loadChildren: () => import('./modules-v2/auditorium/auditorium.routes').then(m => m.routes),
                        data: {
                            authorities: [...AUDITORIUM_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Auditorium v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        path: 'membership',
                        loadChildren: () => import('./membership/membership.routes').then(r => r.routes),
                        data: {
                            authorities: [...MEMBERSHIP_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Membership'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Membership v2 (docs/MODULES_V2.md, Batch 2): parallel
                        // standalone route, same guards/authorities as the
                        // legacy `membership` host above. Legacy screens at
                        // /app/membership/* untouched and default. No menu entry
                        // yet: reached by URL (/app/membership-v2) for owner
                        // testing.
                        path: 'membership-v2',
                        loadChildren: () => import('./modules-v2/membership/membership.routes').then(m => m.routes),
                        data: {
                            authorities: [...MEMBERSHIP_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Membership v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Workstream E phase 2: V2 report pages. Authorities
                        // cover the voucher-backed Collection Report (mirror
                        // of the backend `accountVoucherAccess` guard - the
                        // new ACCOUNT role or any existing admin/report
                        // authority) PLUS the pooja-report roles for the
                        // jsPDF Pooja Collection/Bookings pages, so every
                        // user who sees the menu entries passes this guard.
                        // STORE_MODULE_ROLES added for the Stock Collection
                        // (V2) page (legacy collection-summary roles).
                        path: 'reports-v2',
                        loadChildren: () => import('./booking-v2/reports/reports-v2.routes').then(m => m.routes),
                        data: {
                            authorities: [...ACCOUNTANT_ROLES, ...COMMON_ROLES, 'AUDITOR_ACCESS', ...REPORT_MODULE, 'ACCOUNTING_FULL_ACCESS', ...POOJA_REPORT_ROLES, 'POOJA_BILLING_ACCESS', ...STORE_MODULE_ROLES, ...EXPENSE_MODULE_ROLES],
                            pageTitle: 'Reports v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        path: 'reports',
                        loadChildren: () => import('./reports/reports.module').then(m => m.ReportsModule),
                        data: {
                            authorities: [...REPORT_MODULE_ACCESS],
                            pageTitle: 'Reports'
                        },
                        canActivateChild: [UserRouteAccessGuard, ExpiryGuard], canLoad: [UserRouteAccessGuard, ExpiryGuard]
                    },
                    {
                        path: 'stoda',
                        loadChildren: () => import('./stoda/stoda.module').then(m => m.StodaModule),
                        data: {
                            authorities: [...REPORT_MODULE_ACCESS],
                            pageTitle: 'Vazhipadu Head'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]

                    },

                    {
                        path: 'help',
                        loadChildren: () => import('./help/help.module').then(m => m.HelpModule),
                        data: {
                            authorities: [...COMMON_ROLES],
                            pageTitle: 'Help'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]

                    },
                    {
                        path: 'settings',
                        loadChildren: () => import('./stoda-settings/stoda-settings.module').then(m => m.StodaSettingsModule),
                        data: {
                            authorities: [...REPORT_MODULE_ACCESS],
                            pageTitle: 'Settings'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]

                    },
                    {
                        path: 'salary',
                        loadChildren: () => import('./salary/salary.routes').then(route => route.routes),
                        data: {
                            authorities: [...PAYROLL_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Salary'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]

                    },
                    {
                        // Salary v2 (docs/MODULES_V2.md, Batch 3): parallel
                        // standalone salary-payment route, same guards/authorities
                        // as the legacy `salary` host above. Legacy screens at
                        // /app/salary/* untouched and default. No menu entry yet:
                        // reached by URL (/app/salary-v2) for owner testing.
                        path: 'salary-v2',
                        loadChildren: () => import('./modules-v2/salary/salary.routes').then(m => m.routes),
                        data: {
                            authorities: [...PAYROLL_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Salary v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Salary Advance v2 (WS-H, docs/MODULES_V2.md): parallel
                        // standalone advance-payment route, same guards/authorities
                        // as the legacy `salary` host. Legacy modals untouched. No
                        // menu entry yet: reached by URL (/app/salary-advance-v2/advance).
                        path: 'salary-advance-v2',
                        loadChildren: () => import('./modules-v2/salary/salary-secondary.routes').then(m => m.routes),
                        data: {
                            authorities: [...PAYROLL_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Salary Advance v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Salary Loan v2 (WS-H): reached by URL (/app/salary-loan-v2/loan).
                        path: 'salary-loan-v2',
                        loadChildren: () => import('./modules-v2/salary/salary-secondary.routes').then(m => m.routes),
                        data: {
                            authorities: [...PAYROLL_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Salary Loan v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    },
                    {
                        // Salary Bata v2 (WS-H): reached by URL (/app/salary-bata-v2/bata).
                        path: 'salary-bata-v2',
                        loadChildren: () => import('./modules-v2/salary/salary-secondary.routes').then(m => m.routes),
                        data: {
                            authorities: [...PAYROLL_MODULE_ROLES, ...COMMON_ROLES],
                            pageTitle: 'Salary Bata v2'
                        },
                        canActivateChild: [UserRouteAccessGuard], canLoad: [UserRouteAccessGuard]
                    }
                ]
            }
        ]
    },
    { path: 'error', component: ErrorComponent },
    { path: '**', component: PageNotFoundComponent }
];

@NgModule({
    imports: [],
    // exports: [RouterModule],
})
export class AppRoutingModule {
    // static GuestGuard, TempleAdminGuard, AdminGuard, DevoteeGuard = [TempleAdminGuard, AdminGuard, DevoteeGuard];
    static COMPONENTS = [
        AuthenticatedComponent, HomeComponent,
        PageNotFoundComponent,
        ErrorComponent
    ];
    static RESOLVERS = [SearchResolverService, UserRouteAccessGuard, PanchangamResolverService, GuestGuard, AuthGuard];
}
